import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { previewCoachingVideoReference } from "@/lib/api";
import { ownedRequest } from "@/features/coach/assist/useAssistContext";
import type { DraftContext } from "@/features/coach-review/composerDrafts";
import type { MatchSlot } from "@/features/matches/matchSlots";
import { readReviewCarry, saveReviewCarry } from "./reviewState";
export function useReviewRecordingReturn(
  owner: DraftContext | null,
  captureId: string,
  matchId: string,
  slot: MatchSlot,
  video: React.RefObject<HTMLVideoElement>,
) {
  const carry = owner ? readReviewCarry(owner, { captureId }) : null;
  const alive = useRef(true);
  const currentSelection = useRef({ matchId, slot });
  currentSelection.current = { matchId, slot };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return useMutation({
    meta: { composerContext: owner },
    retry: false,
    mutationFn: async () => {
      if (
        !alive.current ||
        !owner ||
        !carry ||
        carry.kind !== "feedback" ||
        carry.origin.captureId !== captureId
      )
        throw new Error(
          "Inbox origin unavailable. Return and refresh the private draft.",
        );
      const el = video.current;
      if (
        !el ||
        el.readyState < 1 ||
        !Number.isFinite(el.duration) ||
        el.duration <= 0 ||
        !Number.isFinite(el.currentTime) ||
        el.currentTime < 0 ||
        el.currentTime > el.duration
      )
        throw new Error(
          "Wait for the actual recording and select its playhead.",
        );
      const reference = await ownedRequest(owner, () =>
        previewCoachingVideoReference(
          carry.origin.draftId,
          {
            match_id: matchId,
            slot,
            timestamp_seconds: el.currentTime,
            expected_draft_revision: carry.origin.draftRevision,
            confirmation_id: carry.origin.confirmationId,
            expected_confirmation_revision: carry.origin.confirmationRevision,
          },
          { teamId: owner.teamId, seasonId: owner.seasonId },
        ),
      );
      if (
        !alive.current ||
        currentSelection.current.matchId !== matchId ||
        currentSelection.current.slot !== slot
      )
        throw new Error(
          "Recording selection changed. Review the current recording again.",
        );
      const current = readReviewCarry(owner, { captureId });
      if (
        !current ||
        current.kind !== "feedback" ||
        JSON.stringify(current.video) !== JSON.stringify(carry.video) ||
        JSON.stringify(current.origin) !== JSON.stringify(carry.origin)
      )
        throw new Error("Inbox target changed. Return to review again.");
      saveReviewCarry(owner, { ...current, video: reference });
    },
  });
}
